import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import PasswordPairWithRules from "../../../components/PasswordPairWithRules";
import { UIButton } from "../../../components/ui";
import messages from "../../../assets/locale/messages";
import { changeCompanyPasswordRequest, changeClientPasswordRequest } from "../../../store/Auth/actions";
import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import Input from "../../../components/Input";
import {
  passwordHasCharsetRules,
  passwordHasMinLength,
} from "../../../components/PasswordPairWithRules/passwordRules";
import { PASSWORD_REGEX } from "../../../utils/Patterns";

// While company `must_change_password` is true, PrivateRoute / PublicRoute only allow
// `/company/reset-password*` and `/company/change-password` (see `companyRegistration.js`).

const initialValues = { oldPassword: "", newPassword: "", confirmPassword: "" };

const ResetPasswordStep2 = ({ email, authPortal = AUTH_CONTEXT_TYPE.COMPANY }) => {
  const lang = useSelector((state) => state.locale.lang);
  const dispatch = useDispatch();
  const { resetPassword: t } = messages[lang];

  const changePasswordSettledSeq = useSelector(
    (state) => state.auth.changePasswordSettledSeq ?? 0
  );
  const lastHandledSettledSeq = useRef(changePasswordSettledSeq);

  const subtitleParts = t.step2.subtitle.split("{email}");
  const subtitleBefore = subtitleParts[0] || "";
  const subtitleAfter = subtitleParts[1] || "";

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    setSubmitting: setFormSubmitting,
    isValid,
    dirty,
    isSubmitting,
  } = useFormik({
    validateOnMount: true,
    initialValues: { ...initialValues },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("oldPasswordRequired"),
      newPassword: Yup.string()
        .required("newPasswordRequired")
        .matches(PASSWORD_REGEX, ""),
      confirmPassword: Yup.string()
        .required("confirmNewPasswordRequired")
        .oneOf([Yup.ref("newPassword")], "passwordIsNotMatched"),
    }),
    onSubmit: (formValues, { setSubmitting }) => {
      setSubmitting(true);
      const changePasswordAction =
        authPortal === AUTH_CONTEXT_TYPE.CLIENT
          ? changeClientPasswordRequest
          : changeCompanyPasswordRequest;
      dispatch(
        changePasswordAction({
          from: "change",
          data: {
            old_password: formValues.oldPassword,
            new_password: formValues.newPassword,
          },
        }),
      );
    },
  });

  useEffect(() => {
    if (changePasswordSettledSeq === lastHandledSettledSeq.current) {
      return;
    }
    lastHandledSettledSeq.current = changePasswordSettledSeq;
    setFormSubmitting(false);
  }, [changePasswordSettledSeq, setFormSubmitting]);

  const lenOk = passwordHasMinLength(values.newPassword);
  const charsetOk = passwordHasCharsetRules(values.newPassword);

  const showConfirmMatch =
    touched.confirmPassword &&
    values.confirmPassword &&
    values.newPassword === values.confirmPassword &&
    !errors.confirmPassword;

  return (
    <div className="reset-password-flow__step w-100">
      <header className="auth-form-shell__header">
        <h1 className="auth-layout__card__title">{t.step2.title}</h1>
        <p className="auth-layout__card__subtitle mb-0">
          {subtitleBefore}
          <span className="auth-form-shell__email-accent">{email || ""}</span>
          {subtitleAfter}
        </p>
      </header>

      <form className="auth-form-shell__form" onSubmit={handleSubmit} noValidate>
        <Input
          id="reset-password-current"
          name="oldPassword"
          type="password"
          label={t.step2.currentPassword}
          placeholder={t.step2.currentPasswordPlaceholder}
          required
          value={values.oldPassword}
          onChange={(value) => {
            setFieldTouched("oldPassword", true);
            setFieldValue("oldPassword", value);
          }}
          onBlur={handleBlur}
          isInputHasErr={Boolean(touched.oldPassword && errors.oldPassword)}
          errMsg={errors.oldPassword}
          isPasswordInput
          inputWrapperClass="mb-0"
          labelClassName="font-medium mb-2"
        />

        <PasswordPairWithRules
          passwordId="reset-password-new"
          confirmId="reset-password-confirm"
          passwordName="newPassword"
          confirmName="confirmPassword"
          passwordLabel={t.step2.newPassword}
          confirmLabel={t.step2.confirmPassword}
          passwordPlaceholder={t.step2.newPasswordPlaceholder}
          confirmPlaceholder={t.step2.confirmPasswordPlaceholder}
          passwordValue={values.newPassword}
          confirmValue={values.confirmPassword}
          onPasswordChange={(value) => {
            setFieldTouched("newPassword", true);
            setFieldValue("newPassword", value);
          }}
          onConfirmChange={(value) => {
            setFieldTouched("confirmPassword", true);
            setFieldValue("confirmPassword", value);
          }}
          onBlur={handleBlur}
          passwordRequired
          passwordError={errors.newPassword}
          confirmError={errors.confirmPassword}
          passwordTouched={touched.newPassword}
          confirmTouched={touched.confirmPassword}
          rules={[
            {
              label: t.step2.rules.length,
              satisfied:
                values.newPassword.length > 0 ? lenOk : undefined,
            },
            {
              label: t.step2.rules.complexity,
              satisfied:
                values.newPassword.length > 0 ? charsetOk : undefined,
            },
          ]}
          showExternalConfirmMatch
          confirmMatchVisible={Boolean(showConfirmMatch)}
          confirmMatchAriaLabel={t.step2.confirmMatchAria}
        />

        <UIButton
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          disabled={!(isValid && dirty) || isSubmitting}
        >
          {t.reset}
        </UIButton>
      </form>
    </div>
  );
};

ResetPasswordStep2.propTypes = {
  email: PropTypes.string,
  authPortal: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

ResetPasswordStep2.defaultProps = {
  email: "",
  authPortal: AUTH_CONTEXT_TYPE.COMPANY,
};

export default ResetPasswordStep2;
